import {
    CompetitorServiceStatus,
    ICompetitorServiceStatusConfig,
} from '../types/competitorsViewerTypes';
import { TagColor } from '../../../muiComponents/Tag';
import {
    ScraperAttributeStatus,
    ScraperServiceStatus,
} from '../types/scrapedServicesTypes';
import { AttributeDefinitionFieldType } from '../types/zitoneoServicesTypes';

export const competitorServiceStatusConfig: Record<
    CompetitorServiceStatus,
    ICompetitorServiceStatusConfig
> = {
    [CompetitorServiceStatus.OFFERED]: {
        text: CompetitorServiceStatus.OFFERED,
        color: 'green',
    },
    [CompetitorServiceStatus.NOT_OFFERED]: {
        text: CompetitorServiceStatus.NOT_OFFERED,
        color: 'grey',
    },
    [CompetitorServiceStatus.PARITY]: {
        text: CompetitorServiceStatus.PARITY,
        color: 'grey',
    },
    [CompetitorServiceStatus.WE_LEAD]: {
        text: CompetitorServiceStatus.WE_LEAD,
        color: 'green',
    },
    [CompetitorServiceStatus.THEY_LEAD]: {
        text: CompetitorServiceStatus.THEY_LEAD,
        color: 'orange',
    },
};

export const getCompetitorViewerReadStatusConfig = (
    ownHotel: boolean,
    competitor: boolean
) => {
    if (ownHotel && competitor) return CompetitorServiceStatus.PARITY;
    if (ownHotel) return CompetitorServiceStatus.WE_LEAD;
    if (competitor) return CompetitorServiceStatus.THEY_LEAD;
    return CompetitorServiceStatus.NOT_OFFERED;
};

export const scraperAttributeStatusConfig: Record<
    ScraperAttributeStatus,
    TagColor
> = {
    [ScraperAttributeStatus.NEW]: 'green',
    [ScraperAttributeStatus.CONFLICT]: 'orange',
    [ScraperAttributeStatus.MATCHED]: 'grey',
};

export const scraperServiceStatusConfig: Record<
    ScraperServiceStatus,
    TagColor
> = {
    [ScraperServiceStatus.NEW]: 'green',
    [ScraperServiceStatus.EXISTING]: 'grey',
};

const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Infers the field type of a scraped attribute from its stored string value:
 * "true"/"false" as boolean, numeric strings as number, ISO dates as date and
 * anything else (including empty values) as string.
 * @param value Raw attribute value.
 * @returns The inferred field type.
 */
export const inferScraperAttributeFieldType = (
    value: string | null | undefined
): AttributeDefinitionFieldType => {
    const trimmedValue = (value ?? '').trim();
    const lowerCaseValue = trimmedValue.toLowerCase();
    let fieldType = AttributeDefinitionFieldType.STRING;
    if (lowerCaseValue === 'true' || lowerCaseValue === 'false') {
        fieldType = AttributeDefinitionFieldType.BOOLEAN;
    } else if (trimmedValue && Number.isFinite(Number(trimmedValue))) {
        fieldType = AttributeDefinitionFieldType.NUMBER;
    } else if (
        isoDatePattern.test(trimmedValue) &&
        !Number.isNaN(new Date(trimmedValue).getTime())
    ) {
        fieldType = AttributeDefinitionFieldType.DATE;
    }
    return fieldType;
};

/**
 * Converts an attribute value to the string format stored in the database:
 * booleans as "True"/"False", dates as UTC ISO strings and numbers as plain
 * numeric strings. Values that cannot be parsed are returned trimmed.
 * @param value Raw attribute value.
 * @param fieldType Attribute field type.
 * @returns The normalized attribute value.
 */
export const normalizeScraperAttributeValue = (
    value: string | null | undefined,
    fieldType: AttributeDefinitionFieldType
): string => {
    const trimmedValue = (value ?? '').trim();
    let normalizedValue = trimmedValue;
    if (trimmedValue && fieldType === AttributeDefinitionFieldType.BOOLEAN) {
        const lowerCaseValue = trimmedValue.toLowerCase();
        if (lowerCaseValue === 'true') {
            normalizedValue = 'True';
        } else if (lowerCaseValue === 'false') {
            normalizedValue = 'False';
        }
    } else if (
        trimmedValue &&
        fieldType === AttributeDefinitionFieldType.NUMBER
    ) {
        const numericValue = Number(trimmedValue);
        if (Number.isFinite(numericValue)) {
            normalizedValue = numericValue.toString();
        }
    } else if (
        trimmedValue &&
        fieldType === AttributeDefinitionFieldType.DATE
    ) {
        const dateValue = new Date(trimmedValue);
        if (!Number.isNaN(dateValue.getTime())) {
            normalizedValue = dateValue.toISOString();
        }
    }
    return normalizedValue;
};
